import { useEffect, useRef, useState } from 'react';
import type { EntryPatch, MeasurementMetric } from '@tally/lib/types';
import { saveEntry } from '../lib/api.js';

const FIELD_BY_METRIC: Record<MeasurementMetric, keyof EntryPatch> = {
  waist:     'waist_cm',
  chest:     'chest_cm',
  shoulders: 'shoulders_cm',
};

export function MeasurementInline({
  metric,
  label,
  date,
  initial,
  onSaved,
}: {
  metric: MeasurementMetric;
  label: string;
  date: string;
  initial: number | null;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initial != null ? String(initial) : '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setDraft(initial != null ? String(initial) : '');
  }, [initial]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = async () => {
    const trimmed = draft.trim();
    if (trimmed === '' && initial == null) {
      setEditing(false);
      return;
    }
    const parsed = parseFloat(trimmed);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setErr('invalid');
      return;
    }
    if (parsed === initial) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await saveEntry({ date, [FIELD_BY_METRIC[metric]]: parsed } as EntryPatch);
      setEditing(false);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="border border-stone-500 px-3 py-2 flex items-center gap-2">
        <input
          ref={inputRef}
          type="number"
          step="0.1"
          inputMode="decimal"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void commit();
            if (e.key === 'Escape') { setEditing(false); setErr(null); setDraft(initial != null ? String(initial) : ''); }
          }}
          onBlur={() => void commit()}
          disabled={saving}
          placeholder="cm"
          className="flex-1 min-w-0 bg-transparent font-mono text-sm text-stone-100 placeholder:text-stone-600 focus:outline-none"
        />
        <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-stone-500 shrink-0">
          {saving ? 'saving…' : err ?? 'enter ↵'}
        </span>
      </div>
    );
  }

  const display = initial != null ? `${label} · ${initial} cm` : `Log your ${label.toLowerCase()}`;

  return (
    <button
      onClick={() => setEditing(true)}
      className="w-full border border-stone-500 px-3 py-2 text-[11px] uppercase tracking-[0.2em] font-mono text-stone-200 hover:text-white hover:border-stone-300 transition-colors"
    >
      {display}
    </button>
  );
}
